import { Link } from "react-router-dom";
import { Instagram, MapPin, Phone } from "lucide-react";
import {
  CLINIC_ADDRESS,
  CLINIC_HOURS,
  CLINIC_PHONE_DISPLAY,
  INSTAGRAM_URL,
  WHATSAPP_CHAT_URL,
} from "../config";

export const Footer = () => (
  <footer data-testid="footer" className="bg-[#14717A] text-white">
    <div className="mx-auto grid max-w-7xl gap-12 px-6 py-16 sm:px-8 md:grid-cols-3">
      <div>
        <div className="flex items-center gap-3">
          <div>
            <p className="font-heading text-2xl font-semibold sm:text-3xl">Dr. Vishwa's</p>
            <p className="text-xs font-bold tracking-[0.28em] uppercase text-white/60 sm:text-sm">
              Dental Hospital
            </p>
          </div>
        </div>
        <p className="mt-5 max-w-xs text-base leading-relaxed text-white/70 sm:text-lg">
          Crafting healthy and confident smiles with gentle and technology
          driven dental care.
        </p>
        <div className="mt-5 flex gap-3">
          <a
            href={WHATSAPP_CHAT_URL}
            target="_blank"
            rel="noreferrer"
            data-testid="footer-whatsapp"
            aria-label="WhatsApp"
            className="flex h-10 w-10 items-center justify-center rounded-full bg-[#25D366] text-white transition-transform duration-300 hover:-translate-y-1"
          >
            <svg viewBox="0 0 24 24" className="h-5 w-5 fill-current">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
            </svg>
          </a>
          <a
            href={INSTAGRAM_URL}
            target="_blank"
            rel="noreferrer"
            data-testid="footer-instagram"
            aria-label="Instagram"
            className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-tr from-[#f58529] via-[#dd2a7b] to-[#8134af] text-white transition-transform duration-300 hover:-translate-y-1"
          >
            <Instagram className="h-5 w-5" />
          </a>
        </div>
      </div>

      <div>
        <p className="text-sm font-bold tracking-[0.25em] uppercase text-white/60">
          Quick Links
        </p>
        <ul className="mt-5 space-y-3 text-base font-medium">
          <li><Link to="/" data-testid="footer-link-home" className="text-white/80 transition-colors duration-200 hover:text-white">Home</Link></li>
          <li><Link to="/#about-us" data-testid="footer-link-about" className="text-white/80 transition-colors duration-200 hover:text-white">About Us</Link></li>
          <li><Link to="/services" data-testid="footer-link-services" className="text-white/80 transition-colors duration-200 hover:text-white">Services</Link></li>
          <li><Link to="/stories" data-testid="footer-link-stories" className="text-white/80 transition-colors duration-200 hover:text-white">Patient Stories</Link></li>
          <li><Link to="/#reviews" data-testid="footer-link-reviews" className="text-white/80 transition-colors duration-200 hover:text-white">Patient Reviews</Link></li>
          <li><Link to="/#contact" data-testid="footer-link-contact" className="text-white/80 transition-colors duration-200 hover:text-white">Contact Us</Link></li>
          <li><Link to="/book" data-testid="footer-link-book" className="text-white/80 transition-colors duration-200 hover:text-white">Book Appointment</Link></li>
        </ul>
      </div>

      <div>
        <p className="text-sm font-bold tracking-[0.25em] uppercase text-white/60">
          Visit Us
        </p>
        <div className="mt-5 space-y-4 text-base text-white/80">
          <p className="flex items-start gap-3">
            <MapPin className="mt-0.5 h-5 w-5 shrink-0 text-[#7cc7ce]" />
            {CLINIC_ADDRESS}
          </p>
          <p className="flex items-center gap-3">
            <Phone className="h-5 w-5 shrink-0 text-[#7cc7ce]" />
            {CLINIC_PHONE_DISPLAY}
          </p>
          <p className="text-white/60">Open daily · {CLINIC_HOURS}</p>
        </div>
      </div>
    </div>

    <div className="border-t border-white/15">
      <div className="mx-auto max-w-7xl px-6 py-6 sm:px-8">
        <p data-testid="footer-copyright" className="text-center text-sm text-white/80">
          © 2026 Dr. Vishwa's Dental Hospital. All Rights Reserved.
        </p>
      </div>
    </div>
  </footer>
);
